/* ------------------------------------------------------------------------------------------------
   rs-search-fallback.js — the search page's zero-result rescue. rareseeds 2026-10-20.
   Loaded by snippets/section.main-search.liquid as <script type="module" src> on every /search page
   that has a term. It replaces the inline module of 2026-10-16..19 and keeps every behaviour that
   module had (status line, hidden empty block revealed on failure, noscript rule, the capped-count
   re-fetch, module-script re-execution of swapped-in markup, document.title + [data-collection-count]
   updates, history.replaceState to the URL that reproduces what is shown).

   WHY (measured on live, GA4 + ShopifyQL, Oct 17-20 2026): every form submits `title:<word>` per
   word (assets/form.predictive-search.js). Title search stems plurals (title:tomatos = 126 =
   title:tomato) but has ZERO typo tolerance (title:brocolli 0) and no prefix match; `title:a OR
   title:b` works (cabbage OR kale = 26) and parentheses are honoured: title:red (title:cabbage OR
   title:kale) = 1 = the ungrouped form, (title:red title:cabbage) OR title:kale = 10, so OR binds
   tighter than the implicit AND either way. Lowercase `or` is a TERM, not an operator (0 results).
   The unscoped (broad) pass is noisy for an unknown word: brassicaceae = 875 unrelated cards.

   ALL of this runs on the results page, nothing at submit time: the raw typed term must keep reaching
   GA4 (search_term) and Shopify's search log, and a submit-time fetch would race the navigation.

   HOST ELEMENTS (both carry data-terms, data-intents-url, data-vocab-url, data-search-url):
     [data-rs-search-fallback]  the zero-result page: data-section-id for Section Rendering fetches,
                                [data-rs-search-status] (role=status) and [data-rs-search-empty] inside.
     [data-rs-search-hint]      a page WITH results: only the redirects table is consulted, and a
                                match renders a one-line "Looking for X?" hint (never navigates) -
                                without this, catalog / flowers / new / gift certificate (which all
                                match a stray title) could never surface their curated destination.

   ZERO-RESULT PIPELINE (each step only when the previous produced nothing):
     a. redirects[normalized term] (assets/rs-search-intents.json) -> status "Taking you to <label>…"
        and location.replace after window load + 500 ms OR 1,500 ms after the decision, whichever
        comes first (2026-10-27, post-go-live audit PGL-055 - see scheduleRedirect), so the Google
        channel / Meta pixels (sandboxed, late) still emit the search event with the raw term without
        the shopper staring at "0 results" for 4-7 s (30 s when a third-party tag stalled window.load).
        replace, not assign: Back returns to the page before the search. Entries flagged hint:true
        render the hint instead.
     a2. FACET PHRASES (rareseeds 2026-10-21; John: "search for varieties that mature in 60 days and
        stuff like that"). parseFacets() runs the `facets` rule table of the intents JSON over the term
        (longest match first, each matched span removed; numeric days / months / zone buckets in code);
        what is left minus `facet_stopwords` is the crop, matched by matchCollections() against the
        crop collections emitted by section.main-search.liquid (<script data-rs-collections>, from
        shop.metafields.rareseeds.browse_order; titles word-normalised like its Browse bar: "(…)"
        and the part after a comma dropped, seeds/bulbs/tubers/plants removed, singularised).
        facetDestination(): ONE crop collection (exact, or every typed word in its title) ->
        /collections/<handle>?filter.p.m.facet.<key>=<value>… - probed first through the search section
        (title:<family words> OR'd + the same filters) so an empty collection page is never the landing;
        several / none / extra typed words -> /search?q=title:w1 title:w2&type=product&filter… fetched
        through the Section Rendering API and rendered inline under "Showing varieties matching "…"
        that <labels>." (history.replaceState to that URL); no crop words -> /collections/all-seeds-
        products?filter… . A probe that ANSWERS with zero cards relaxes to all-seeds-products + the filters ("No "…"
        names matched; showing every variety that <labels>…") unless the only facets are colour /
        department (then step b runs as before). A span whose words are part of the matched title is
        not a facet (snail vine, balloon flower, garlic bulbs, black-eyed susan) and is dropped again;
        a colour needs a crop match or another facet (purple, cherokee purple stay title searches);
        heat words need a pepper / chile / chili / capsicum token. On a page WITH results the same
        parse renders the hint "Looking for <crop> varieties that <labels>? See them" and never
        navigates; a redirects hint wins when both match.
        Measured on live 2026-10-20/21: filter params combine with the title query on /search
        (title:tomato 126 -> maturity=60+days+or+less 3, two maturity values OR to 93, color=Red 26,
        growth_habit=Determinate 8; title:pepper + heat=Hot 21; title:sunflower 24 -> height=Tall 21;
        title:lettuce + frost=Frost+Hardy 34) and on grid collections (heirloom-tomato-seeds +
        maturity<=60 21; all-seeds-products zone=Zones+3-8 25, height=Tall 212, color=Red 276).
        new_for is NOT an exposed filter (1422 = unfiltered) and has no rule. The landing collections
        flowers / herbs / vegetable-seeds / bulbs / all-seeds / all ignore every filter param and are
        never a destination (they are not in browse_order).
     b. spelling + alias rewrite (title:dwarf (title:cabbage OR title:kale)); a corrected term is
        looked up in redirects again (perinnial -> perennial -> the Perennial facet). A spelling KEY
        with a space is a phrase, applied to the whole term before tokenizing (2026-10-27, PGL-052:
        "egg plant" -> eggplant; see applyPhraseSpelling).
     c. vocab correction (2026-10-26 rewrite, see correctWord): prefix completion first (tomat ->
        tomato), then edit distance <= 1 (4-5 letters) / <= 2 (6+), first letter kept on a tie; a
        word nothing is named is DROPPED and the note says so; any rewrite that finds results is
        rendered, with a "Search instead for" link to the broad original. `ignore` words and words
        with a redirects entry are never corrected (bulk is not a typo of bull).
     d. OR of the positive words (title:w1 OR title:w2 …) for a multi-word query.
     e. broad (unscoped) pass - exactly the 2026-10-16..19 logic, with the cap lowered: any render
        with >= NOISE_CAP results (500, a third of the 1,422-product grid) is "no meaningful match",
        300 for a one-word query. A capped answer is re-fetched up to twice (cache: no-store) because
        Shopify's unscoped count intermittently returns the whole catalogue for a real word ('ancho'
        flipped 2 / 3 / 1000 / 2 across loads, 2026-10-19); the first answer under the cap wins.
     f. in parallel with b-e: /search/suggest …&section_id=search-results for collections / pages /
        articles -> a "Looking for…" block above whatever b-e render, rebuilt from .results__group-1
        only (the section renders every list twice, with ids the page's own dropdown also creates).
     g. empty state only: the predictive products, kept only when the title contains a typed or
        corrected word as a word prefix (the endpoint is fuzzy: 'tumeric' suggests four cucumbers).
   h. empty state only: assets/rs-search-retired.json (tools/gen-search-retired.mjs) - a typed word that
      is a RETIRED variety's word renders "We have carried X before" + the crop collection (2026-10-26).
   One AbortController gives the whole pipeline ~8 s, ending in the empty state. A failed intents /
   vocab fetch is fail-open (that step is skipped), never the empty state on a CDN hiccup.

   Text that comes from the shopper or from JSON is always set with textContent; only Shopify's own
   section renders are inserted as HTML (first party, the same as util.ajax-renderer).

   parseFacets / matchCollections / facetDestination / facetProbeDecision are pure and exported: tools/test/search-facets.test.mjs
   imports this file under node (`node --test tools/test/`), which is why main() only runs when a
   document exists.
   ------------------------------------------------------------------------------------------------ */

const NOISE_CAP = 500
const NOISE_CAP_SINGLE = 300
const BUDGET_MS = 8000
const REDIRECT_GRACE_MS = 500
// 2026-10-27 (PGL-055): the longest a curated / facet redirect waits for window.load before leaving anyway
const REDIRECT_CAP_MS = 1500
const SKU_SHAPE = /^[a-z]{1,4}\d{2,6}[a-z]?$/
// 2026-10-26: plant(s) dropped too - "egg plant" / "tomato plants" are the crop, not a title word
const DROP_WORDS = new Set(['or', 'and', 'the', 'of', 'seeds', 'seed', 'plant', 'plants', 'live', 'heirloom', 'heirlooms', 'organic', 'variety', 'varieties'])

/* ---------- shared text helpers (pure) ---------- */
const fold = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[‘’]/g, "'")
const normalize = (s) =>
  fold(s)
    .replace(/[^a-z0-9'\-\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s+seeds?$/, '')

/* a. (2026-10-27, post-go-live audit PGL-055) The curated / facet redirects used to wait for window
   load + REDIRECT_GRACE_MS so the sandboxed Google channel / Meta pixels (web pixels load late) still
   logged the raw term. Measured with a healthy CDN that was 4.2-7.4 s of "0 results" per rescue
   (sf-003-verify-recipe-redirect.json) and 30 s whenever any async third-party tag held load. Now the
   load listener races a REDIRECT_CAP_MS timer that starts when the decision is made (a module script
   runs after the DOM is parsed, so that is already past DOMContentLoaded); whichever comes first fires
   ONCE. Verified before shipping that the GA4 / Meta search hits still carry the term at the cap
   (evidence/fix-round-2/search/pgl-055-*). Pure: the environment is injected so tools/test can drive it
   with fake timers; main() passes the real document / window. Returns the fire() for tests. */
export function scheduleRedirect(path, env) {
  const e = env || {}
  const setT = e.setTimeout || setTimeout
  const replace = e.replace || ((p) => location.replace(p))
  const grace = e.grace != null ? e.grace : REDIRECT_GRACE_MS
  const cap = e.cap != null ? e.cap : REDIRECT_CAP_MS
  let done = false
  const fire = () => {
    if (done) return
    done = true
    replace(path)
  }
  if (e.readyState === 'complete') {
    setT(fire, grace)
    return fire
  }
  if (e.onLoad) e.onLoad(() => setT(fire, grace))
  setT(fire, cap)
  return fire
}

/* b. phrase spellings (2026-10-27, post-go-live audit PGL-052). A `spelling` KEY with a space is matched
   word-bounded on the folded term BEFORE tokenizing, because tokenize() drops plant(s) and "egg plant"
   reached step b as the one word "egg" -> alias (title:eggplant OR title:egg) -> a cucumber named Egg
   led the 31 eggplants. Longest key first; the key's last word may carry a plural s/es. Returns the text
   unchanged when no phrase matched (so the caller keeps its original token list). Pure, exported. */
export function applyPhraseSpelling(spelling, text) {
  const keys = Object.keys(spelling || {})
    .filter((k) => k.indexOf(' ') !== -1)
    .sort((a, b) => b.length - a.length)
  if (!keys.length) return text
  const folded = ' ' + fold(text).replace(/\s+/g, ' ').trim() + ' '
  let s = folded
  for (const k of keys) {
    const re = new RegExp('(^|\\s)' + k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '(?:es|s)?(?=\\s)', 'g')
    s = s.replace(re, '$1' + spelling[k])
  }
  return s === folded ? text : s.trim()
}

/* ================================================================================================
   a2. FACET PHRASES (rareseeds 2026-10-21) - pure, exported. See the header and the `_doc_facets`
   block of assets/rs-search-intents.json. Nothing below touches the DOM.
   ================================================================================================ */
const FACET_PARAM = 'filter.p.m.facet.'
const ALL_SEEDS_PATH = '/collections/all-seeds-products'
const MATURITY_BUCKETS = ['60 days or less', '61-80 days', '81-100 days', 'Over 100 days']
const MATURITY_TOPS = [60, 80, 100]
// words dropped from both the typed crop and a collection title before they are compared
const CROP_STOP = new Set(['seeds', 'seed', 'bulbs', 'bulb', 'plants', 'plant', 'tubers', 'tuber', 'live', 'and', 'or', 'of', 'the', 'a', 'an'])
// "at or below" prefixes for a number of days / months: every bucket up to N's is included
const LE_WORDS = 'under|less than|fewer than|within|inside|in|before|by|up to|at most|no more than|max(?:imum)?'
// "at or above" (2026-10-21 review #2): a strict prefix (over 100 days = > 100) or an inclusive one
// (at least 90 = >= 90); the suffixes "or more / or longer / or later" and "90+" are inclusive.
// Every bucket from N's upward is included.
const GE_STRICT_WORDS = 'over|more than|longer than|above'
const GE_WORDS = 'at least|minimum|min'
const MONTH_NUM = '[1-6]|one|two|three|four|five|six'
// groups: 1 LE prefix, 2 strict GE prefix, 3 inclusive GE prefix, 4 lo, 5 "+", 6 hi, 7 LE suffix, 8 GE suffix
const DAYS_RE = new RegExp(
  '(?:\\b(?:(' + LE_WORDS + ')|(' + GE_STRICT_WORDS + ')|(' + GE_WORDS + '))\\s+)?(?:(?:about|around|approx(?:imately)?|only|just)\\s+)?\\b(\\d{2,3})(\\+)?(?:\\s*(?:-|to)\\s*(\\d{2,3}))?\\s*-?\\s*days?\\b(?:\\s+(?:or|and)\\s+(?:(less|fewer|under|sooner|quicker)|(more|longer|later))\\b)?',
  'gi'
)
// same groups; a "3-4 months" range is read as one span (review #7: "3-" used to leak into the crop)
const MONTHS_RE = new RegExp(
  '(?:\\b(?:(' + LE_WORDS + ')|(' + GE_STRICT_WORDS + ')|(' + GE_WORDS + '))\\s+)?(?:(?:about|around)\\s+)?\\b(' + MONTH_NUM + ')(\\+)?(?:\\s*(?:-|to)\\s*(' + MONTH_NUM + '))?\\s*months?\\b(?:\\s+or\\s+(?:(less|fewer|under)|(more|longer|later))\\b)?',
  'gi'
)
const ZONE_RE = /\b(?:usda\s+)?(?:hardiness\s+)?zones?\s*(\d{1,2})[ab]?(?:\+|\b)(?:\s*(?:-|to|through)\s*(\d{1,2})[ab]?\b)?/gi
const MONTH_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6 }
const PEPPER_RE = /\b(?:peppers?|chil(?:e|i|li)s?|chil(?:l)?ies|capsicums?)\b/
const HANDLE_RE = /^[a-z0-9][a-z0-9-]*$/
// review #5: a rule hit directly after a negation word is not that facet ("not tall", "non-climbing");
// tested against the text BEFORE the hit. "not frost hardy" / "not hot" / "no heat" are rules of
// their own and, being longer spans, win over the un-negated rule in the same pass.
const NEG_BEFORE_RE = /\b(?:not|non|no|never|without)[\s-]*$/

// the Browse-bar singular: tomatoes -> tomato, radishes -> radish, daisies -> daisy, cress stays
const singularCrop = (w) => {
  if (w.length <= 3) return w
  if (/(?:sses|oes|hes|xes)$/.test(w)) return w.slice(0, -2)
  if (/ies$/.test(w)) return w.slice(0, -3) + 'y'
  if (/ss$/.test(w)) return w
  if (/s$/.test(w)) return w.slice(0, -1)
  return w
}
// a title or a typed phrase -> its comparable words. Hyphens split (black-eyed = black eyed) so a
// colour rule's span can be recognised as part of a name.
// Possessives go on both sides (baby's breath = babys breath, review #7).
const cropWords = (text, keepStop) =>
  fold(text)
    .replace(/\(.*?\)/g, ' ')
    .replace(/'s\b/g, '')
    .replace(/[^a-z0-9'\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && (keepStop || !CROP_STOP.has(w)))
    .map(singularCrop)
// review #7: en/em dashes read as hyphens, possessive 's dropped (florist's -> florist), and a hyphen
// survives only between two digits (60-70 days, 3-4 months), a "+" only after a digit; every other one is a space
// (semi-determinate, black-eyed, "sweet - no heat"). The leading "-word" negation guard of
// parseFacets runs on fold(term), not on this output.
const facetNormalize = (s) =>
  fold(s)
    .replace(/[–—]/g, '-')
    .replace(/'s\b/g, '')
    .replace(/[^a-z0-9'\-+\s]/g, ' ')
    .replace(/(^|[^0-9])\++/g, '$1 ') // "90+" / "zone 3+" keep their plus; any other one is punctuation
    .replace(/-/g, (m, i, str) => (/\d/.test(str[i - 1] || '') && /\d/.test(str[i + 1] || '') ? '-' : ' '))
    .replace(/\s+/g, ' ')
    .trim()
const bucketOf = (n) => (n <= 60 ? 0 : n <= 80 ? 1 : n <= 100 ? 2 : 3)
const bucketRange = (lo, hi) => MATURITY_BUCKETS.slice(bucketOf(lo), bucketOf(hi) + 1)
const bucketsUpTo = (n) => MATURITY_BUCKETS.slice(0, bucketOf(n) + 1)
const bucketsFrom = (n) => MATURITY_BUCKETS.slice(bucketOf(n))
const upToLabel = (n) => {
  const b = bucketOf(n)
  return b === 3 ? 'have a listed days to maturity' : 'mature in ' + MATURITY_TOPS[b] + ' days or less'
}
// the numeric part of a days / months match -> { lo, hi, le, ge, strict } (groups shared by both regexes)
const numericBounds = (m, toNumber) => {
  const lo = toNumber(m[4])
  const hi = m[6] ? Math.max(lo, toNumber(m[6])) : lo
  return { lo, hi, le: !!(m[1] || m[7]), ge: !!(m[2] || m[3] || m[5] || m[8]), strict: !!m[2] }
}
const longestMatch = (re, text) => {
  re.lastIndex = 0
  let best = null
  let m
  while ((m = re.exec(text))) {
    if (!best || m[0].length > best[0].length) best = m
    if (!m[0].length) re.lastIndex++
  }
  return best
}
const zoneValues = (zones, lo, hi) =>
  zones.filter((z) => {
    const m = /(\d+)[ab]?(?:\s*-\s*(\d+)[ab]?)?\s*(\+)?/.exec(z)
    if (!m) return false
    const a = parseInt(m[1], 10)
    const b = m[3] ? 99 : m[2] ? parseInt(m[2], 10) : a
    return a <= hi && b >= lo
  })
const hitOf = (m, rule, values, label) => ({ index: m.index, length: m[0].length, span: m[0], key: rule.key, values, label, needs: rule.needs || null })

const matchRule = (rule, text, zones) => {
  if (rule.kind === 'maturity_days') {
    const m = longestMatch(DAYS_RE, text)
    if (!m) return null
    const { lo, hi, le, ge, strict } = numericBounds(m, (s) => parseInt(s, 10))
    if (le) return hitOf(m, rule, bucketsUpTo(hi), upToLabel(hi))
    // "over 100 days" is > 100 (its bucket starts at 101); "at least 90" / "90 days or more" / "90+" include 90
    if (ge) return hitOf(m, rule, bucketsFrom(strict ? lo + 1 : lo), 'mature in ' + (strict ? 'more than ' + lo + ' days' : lo + ' days or more'))
    if (hi > lo) return hitOf(m, rule, bucketRange(lo, hi), 'mature in ' + lo + '-' + hi + ' days')
    const b = bucketOf(lo)
    return hitOf(m, rule, [MATURITY_BUCKETS[b]], 'mature in ' + MATURITY_BUCKETS[b].toLowerCase())
  }
  if (rule.kind === 'maturity_months') {
    const m = longestMatch(MONTHS_RE, text)
    if (!m) return null
    const { lo, hi, le, ge, strict } = numericBounds(m, (s) => MONTH_WORDS[s] || parseInt(s, 10))
    if (le) return hitOf(m, rule, bucketsUpTo(hi * 30), upToLabel(hi * 30))
    const months = hi > lo ? lo + '-' + hi + ' months' : lo + ' month' + (lo > 1 ? 's' : '')
    // "over 3 months" starts after month 3's days; "3 months or more" includes month 3's own range
    if (ge) return hitOf(m, rule, bucketsFrom(strict ? lo * 30 + 1 : (lo - 1) * 30 + 1), 'mature in ' + (strict ? 'more than ' + months : months + ' or more'))
    return hitOf(m, rule, bucketRange((lo - 1) * 30 + 1, hi * 30), 'mature in about ' + months)
  }
  if (rule.kind === 'zone') {
    const m = longestMatch(ZONE_RE, text)
    if (!m) return null
    const lo = parseInt(m[1], 10)
    const hi = m[2] ? Math.max(lo, parseInt(m[2], 10)) : lo
    const values = zoneValues(zones, lo, hi)
    if (!values.length) return null
    return hitOf(m, rule, values, 'grow in zone ' + (hi > lo ? lo + '-' + hi : lo))
  }
  if (!rule.match || !rule.key || !Array.isArray(rule.values)) return null
  let re = rule._re
  if (!re) {
    try {
      re = rule._re = new RegExp('\\b(?:' + rule.match + ')\\b', 'gi')
    } catch (e) {
      return null
    }
  }
  const m = longestMatch(re, text)
  return m ? hitOf(m, rule, rule.values, rule.label || rule.values.join(' or ')) : null
}

/* typed crop words against the crop collections ([title, handle] pairs). exact = every typed word
   in the title and every family word (before the comma) typed; aOk = every typed word in the title
   (bok choy -> Bok Choy / Tatsoi); bOk = every family word typed but more was typed (cherry tomato).
   Exact matches win, then aOk, then bOk. */
export function matchCollections(crop, collections) {
  if (!crop || !crop.length || !Array.isArray(collections)) return []
  const exact = []
  const aOnly = []
  const bOnly = []
  for (const c of collections) {
    const title = Array.isArray(c) ? c[0] : c && c.title
    const handle = Array.isArray(c) ? c[1] : c && c.handle
    // the handle becomes a path segment: only a Shopify-shaped one is ever navigated to (review #9)
    if (!title || !handle || !HANDLE_RE.test(handle)) continue
    const words = cropWords(title)
    const family = cropWords(title.split(',')[0])
    if (!family.length) continue
    const aOk = crop.every((w) => words.includes(w))
    const bOk = family.every((w) => crop.includes(w))
    if (!aOk && !bOk) continue
    const hit = { title, handle, family, words: cropWords(title, true), exact: aOk && bOk, aOk, bOk }
    if (hit.exact) exact.push(hit)
    else if (aOk) aOnly.push(hit)
    else bOnly.push(hit)
  }
  return exact.length ? exact : aOnly.length ? aOnly : bOnly
}

/* term -> { filters: [{key, values, label, spans}], crop: [words], consumed, clause }. `collections`
   is optional: with it a colour's needs:'crop' is settled against the real list, without it any crop
   word counts. */
export function parseFacets(term, intents, collections) {
  const none = (tokens) => ({ filters: [], crop: tokens || [], consumed: false, clause: '' })
  const rules = intents && Array.isArray(intents.facets) ? intents.facets : []
  const text0 = facetNormalize(term)
  // a "-word" exclusion is the title pipeline's business; facetNormalize drops hyphens, so look at the raw term
  if (!rules.length || !text0 || /(^|\s)-\S/.test(fold(term))) return none(text0 ? text0.split(' ') : [])
  const zones = intents && Array.isArray(intents.zones) ? intents.zones : []
  const stop = new Set(intents && Array.isArray(intents.facet_stopwords) ? intents.facet_stopwords : [])
  const pepper = PEPPER_RE.test(text0)
  let text = ' ' + text0 + ' '
  const found = []
  for (let pass = 0; pass < 24; pass++) {
    let best = null
    for (const rule of rules) {
      if (rule.needs === 'pepper' && !pepper) continue
      const hit = matchRule(rule, text, zones)
      if (hit && (!best || hit.length > best.length)) best = hit
    }
    if (!best) break
    const before = text.slice(0, best.index)
    const neg = NEG_BEFORE_RE.exec(before)
    if (neg) {
      // review #5: "not tall", "non-climbing" - not that facet, and not crop words either
      text = before.slice(0, neg.index) + ' ' + text.slice(best.index + best.length)
      continue
    }
    found.push(best)
    text = text.slice(0, best.index) + ' ' + text.slice(best.index + best.length)
  }
  const tokens = text.split(/\s+/).filter(Boolean)
  if (!found.length) return none(tokens)
  const crop = tokens.filter((w) => !stop.has(w))
  const free = found.filter((f) => f.needs !== 'crop')
  const cropOk =
    free.length > 0 ||
    (crop.length > 0 && (!collections || matchCollections(crop.map(singularCrop), collections).length > 0))
  let kept = cropOk ? found : free
  if (!kept.length) return none(tokens)
  // review #8: a product has ONE department, so "flower bulbs" is not Flower Seeds OR Bulbs - the
  // last-typed department span (the head noun) is the one that counts. Indices compare in text order:
  // removing a span only shifts what follows it leftwards.
  const departments = kept.filter((f) => f.key === 'department')
  if (departments.length > 1) {
    const last = departments.reduce((a, b) => (b.index >= a.index ? b : a))
    kept = kept.filter((f) => f.key !== 'department' || f === last)
  }
  const filters = []
  for (const f of kept) {
    const same = filters.find((x) => x.key === f.key)
    if (!same) {
      filters.push({ key: f.key, values: f.values.slice(), label: f.label, spans: [f.span.trim()] })
      continue
    }
    for (const v of f.values) if (!same.values.includes(v)) same.values.push(v)
    if (!same.label.includes(f.label)) same.label += ' or ' + f.label
    same.spans.push(f.span.trim())
  }
  return { filters, crop, consumed: true, clause: filters.map((f) => f.label).join(' and ') }
}

const encodeValue = (v) => encodeURIComponent(v).replace(/%20/g, '+')
const filterQuery = (filters) =>
  filters.map((f) => f.values.map((v) => FACET_PARAM + f.key + '=' + encodeValue(v)).join('&')).join('&')
const titleQuery = (words) =>
  words.map((w) => (SKU_SHAPE.test(w) ? 'variants.sku:' : 'title:') + w).join(' ')
const searchQuery = (q) => encodeURIComponent(q).replace(/%20/g, '+').replace(/%3A/gi, ':')

/* parsed facets + the crop collections -> where to send the shopper, or null when nothing is left.
   { kind: 'collection' | 'search' | 'all', url, relaxUrl, relaxable, probeQ, crop (display), clause,
     collection, filters } */
export function facetDestination(parsed, collections) {
  // 2026-10-26: heat is a pepper facet. "super hot" / "hottest" with no crop word means peppers;
  // "carolina reaper" (a variety name, no collection) means peppers too; "hottest tomato" names a
  // real other crop and is not a heat search at all (review #6) - fall through to the title steps.
  if (parsed && parsed.consumed && parsed.filters && parsed.filters.some((f) => f.key === 'heat')) {
    const crop = parsed.crop || []
    const pepper = crop.some((w) => /^(?:peppers?|chil(?:e|i|li)s?|chil(?:l)?ies|capsicums?)$/.test(w))
    if (!pepper) {
      if (crop.length && Array.isArray(collections) && matchCollections(crop.map(singularCrop), collections).length) return null
      parsed = Object.assign({}, parsed, { crop: ['pepper'] })
    }
  }
  if (!parsed || !parsed.consumed || !parsed.filters.length) return null
  const crop = parsed.crop.slice()
  const singular = crop.map(singularCrop)
  let filters = parsed.filters.map((f) => ({ key: f.key, values: f.values.slice(), label: f.label, spans: f.spans.slice() }))
  const matches = matchCollections(singular, collections)
  let collection = matches.length === 1 ? matches[0] : null
  if (collection) {
    // a span that is part of the collection's own name is not an attribute (snail VINE, balloon
    // FLOWER, garlic BULBS, BLACK-eyed susan): drop that filter again
    filters = filters.filter((f) => !f.spans.some((s) => cropWords(s, true).every((w) => collection.words.includes(w))))
    if (!filters.length) return null
  }
  const clause = filters.map((f) => f.label).join(' and ')
  const fq = filterQuery(filters)
  const relaxUrl = ALL_SEEDS_PATH + '?' + fq
  const relaxable = filters.some((f) => f.key !== 'color' && f.key !== 'department')
  const base = { clause, filters, relaxUrl, relaxable, collection: null }
  if (!crop.length) return Object.assign(base, { kind: 'all', url: relaxUrl, crop: '', probeQ: null })
  if (collection && (collection.exact || collection.aOk)) {
    // review #6: a comma family ("Bean Seeds, Garbanzo") is a sub-collection - probing title:bean
    // would pass on the family's names and land on an empty garbanzo page, and the status would
    // say "bean". Probe with the typed words AND'd (singular: title:bean prefixes "beans" too) and
    // say what was typed. A plain family (Tomato Seeds, Bok Choy / Tatsoi) keeps the OR'd title words.
    const sub = collection.title.includes(',')
    return Object.assign(base, {
      kind: 'collection',
      url: '/collections/' + collection.handle + '?' + fq,
      crop: sub ? crop.join(' ') : collection.family.join(' '),
      collection: { title: collection.title, handle: collection.handle },
      // non-emptiness check through the search section: the family words OR'd (bok OR choy OR tatsoi)
      probeQ: sub ? titleQuery(singular) : collection.family.map((w) => 'title:' + w).join(' OR '),
    })
  }
  const q = titleQuery(crop)
  return Object.assign(base, {
    kind: 'search',
    url: '/search?q=' + searchQuery(q) + '&type=product&' + fq,
    crop: crop.join(' '),
    probeQ: q,
  })
}

/* What the zero page does with the probe of a facet destination (review #1). `probe` is
   { ok: boolean, res } - ok only when the section render came back; a thrown / aborted fetch is
   ok:false. Returns 'navigate' (the collection page has these varieties), 'render' (show the
   /search render inline), 'relax' (the probe answered with ZERO cards and the facets are worth the
   whole catalog), or 'fallthrough' (step b runs: a failed probe is not evidence of zero names, and a
   colour / department alone is a better title search). */
export function facetProbeDecision(dest, probe) {
  if (!dest || !probe || !probe.ok || !probe.res || !probe.res.doc) return 'fallthrough'
  const res = probe.res
  if (res.content && res.cards > 0) return dest.kind === 'collection' ? 'navigate' : 'render'
  // an answered render with no grid IS the zero answer: the search page renders no
  // .main-search__content when nothing matches (2026-10-21: "container cucumbers" used to fall
  // through to the any-word OR instead of relaxing to every container variety)
  return dest.relaxable ? 'relax' : 'fallthrough'
}


/* ------------------------------------------------------------------------------------------------
   Spelling correction against the title vocabulary (assets/rs-search-vocab.json: word -> count).
   2026-10-26 rewrite after the search audit (John: "results need to render on misspellings"):
     1. PREFIX COMPLETION first - `title:` has no prefix matching, so "tomat" / "cucum" / "cuc" found
        nothing; the most frequent title word starting with the typed letters wins (tomato, cucumber).
     2. Optimal-string-alignment distance <= 1 for 4-5 letters, <= 2 from 6 letters (was 7); a
        candidate keeping the typed FIRST letter wins a tie (typos rarely change it), then frequency.
     3. The caller renders the correction whenever it yields results - a two-edit fix used to be
        offered only as a "Did you mean" link, so "brocolli" showed nothing.
   Returns { w, d, n, kind: 'prefix' | 'edit' } or null.
   ------------------------------------------------------------------------------------------------ */
export function osaDistance(a, b, max) {
  const la = a.length
  const lb = b.length
  if (Math.abs(la - lb) > max) return max + 1
  let prev2 = null
  let prev = []
  for (let j = 0; j <= lb; j++) prev[j] = j
  for (let i = 1; i <= la; i++) {
    const cur = [i]
    let rowMin = i
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1)
      cur[j] = v
      if (v < rowMin) rowMin = v
    }
    if (rowMin > max) return max + 1
    prev2 = prev
    prev = cur
  }
  return prev[lb]
}
export function correctWord(vocab, ignore, w) {
  if (!vocab || w.length < 3 || !/^[a-z][a-z'-]*$/.test(w) || (ignore && ignore.has(w))) return null
  // a word that IS a title word needs no correction (pea must not become peach)
  if (Object.prototype.hasOwnProperty.call(vocab, w)) return null
  let best = null
  for (const cand in vocab) {
    if (cand.length > w.length && cand.indexOf(w) === 0) {
      const n = vocab[cand]
      if (!best || n > best.n) best = { w: cand, d: 0, n, kind: 'prefix' }
    }
  }
  // review 2026-10-26 #2: a 3-letter fragment completes only to a COMMON title word (san -> sang,
  // hop -> hopi, bra -> brandywine were all n <= 6 one-offs); 4+ letters may complete to anything
  if (best && (w.length >= 5 || best.n >= 8)) return best
  best = null
  if (w.length < 4) return null
  const max = w.length >= 6 ? 2 : 1
  let weak = null // the nearest candidate the frequency floor refused: a rare real word, not a typo
  for (const cand in vocab) {
    const n = vocab[cand]
    const d = osaDistance(w, cand, max)
    if (d > max) continue
    // typos almost never change the first letter: lantana must not become montana
    if (cand.charAt(0) !== w.charAt(0)) continue
    if ((d === 2 && n < 4) || (d === 1 && w.length <= 5 && n < 2)) {
      if (!weak || d < weak.d) weak = { w: cand, d, n, kind: 'edit', weak: true }
      continue
    }
    if (!best || d < best.d || (d === best.d && n > best.n)) {
      best = { w: cand, d, n, kind: 'edit', first: true }
    }
  }
  return best || weak
}

const host = typeof document !== 'undefined' ? document.querySelector('[data-rs-search-fallback], [data-rs-search-hint]') : null
if (host) main(host)

function main(host) {
  const ctrl = new AbortController()
  const signal = ctrl.signal
  const timer = setTimeout(() => ctrl.abort(), BUDGET_MS)
  const isZero = host.hasAttribute('data-rs-search-fallback')
  const terms = host.dataset.terms || ''
  const searchPath = host.dataset.searchUrl || '/search'
  const status = host.querySelector('[data-rs-search-status]')
  const empty = host.querySelector('[data-rs-search-empty]')
  const sectionId = host.dataset.sectionId
  let rendered = false
  let didYouMean = null
  // the element the "Looking for…" block is inserted before: the host, or the note that replaces it
  let top = host
  // the words a later step searched instead of the typed ones, for the notes ("tumeric" -> "turmeric")
  const words = (tokens) => tokens.map((t) => (t.neg ? '-' + t.w : t.w)).join(' ')
  const searchedQ = (new URL(location.href).searchParams.get('q') || '').trim()

  const setStatus = (text) => {
    if (status) status.textContent = text
  }

  /* ---------- small helpers ---------- */
  const el = (tag, className, text) => {
    const node = document.createElement(tag)
    if (className) node.className = className
    if (text != null) node.textContent = text
    return node
  }
  const fetchJson = (url) =>
    fetch(url, { headers: { Accept: 'application/json' }, signal })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
  // rareseeds 2026-10-21: the crop collections for step a2, emitted by section.main-search.liquid
  const collectionsList = () => {
    const node = document.querySelector('script[data-rs-collections]')
    if (!node) return []
    try {
      const list = JSON.parse(node.textContent)
      return Array.isArray(list) ? list : []
    } catch (e) {
      return []
    }
  }
  const tokenize = (s) => {
    const out = []
    for (const raw of fold(s).split(/\s+/)) {
      const neg = raw.charAt(0) === '-'
      const w = raw
        .replace(/^[-+]+/, '')
        .replace(/[^a-z0-9'\-]/g, '')
        .replace(/^[-']+|[-']+$/g, '')
      if (!w) continue
      if (!neg && DROP_WORDS.has(w)) continue
      out.push({ w, neg, alt: null })
    }
    return out
  }
  const field = (w) => (SKU_SHAPE.test(w) ? 'variants.sku:' : 'title:') + w
  const scoped = (tokens) =>
    tokens
      .map((t) => {
        if (t.neg) return '-' + field(t.w)
        if (t.alt && t.alt.length > 1) return '(' + t.alt.map(field).join(' OR ') + ')'
        if (t.alt && t.alt.length === 1) return field(t.alt[0])
        return field(t.w)
      })
      .join(' ')
  // for notes and the h1: "brassica (cabbage, kale, …)" for an alias, the word itself otherwise
  const shown = (tokens) =>
    tokens.map((t) => (t.neg ? '-' + t.w : t.alt ? t.w + ' (' + t.alt.join(', ') + ')' : t.w)).join(' ')
  const hasAlias = (tokens) => tokens.some((t) => t.alt)
  const positives = (tokens) => tokens.filter((t) => !t.neg)
  // rareseeds 2026-10-27 (post-go-live audit SF-002): `broad` marks a URL whose q is UNSCOPED on
  // purpose (the step-e broad pass, "Search instead for"). section.main-search.liquid now rescopes
  // any unscoped /search?q= entry at parse time (legacy /catalogsearch 301s, the SearchAction target,
  // shared links all arrive raw and flooded 671-1002 cards) and skips only when the URL carries
  // rs=broad - without the marker, reload / sort / page 2 of a broad answer and that link would loop
  // back through the whole 8 s pipeline (scoped page 2 = 0 -> rescue -> broad page 1). Shopify
  // ignores the param (measured: /search?q=zzzqqq&type=product&rs=broad = 671, same as without).
  // Scoped hrefs (pageHref(scoped(...)), the facet renders) stay clean.
  const SCOPED_Q = /(^|\s)-?(?:title|variants\.sku):/
  const pageUrl = (q, broad) => {
    const u = new URL(location.href)
    u.hash = ''
    u.searchParams.set('q', q)
    u.searchParams.delete('page')
    u.searchParams.delete('section_id')
    if (broad) u.searchParams.set('rs', 'broad')
    else u.searchParams.delete('rs')
    return u
  }
  const pageHref = (q, broad) => {
    const u = pageUrl(q, broad)
    return u.pathname + u.search
  }
  const entryOf = (v) =>
    typeof v === 'string' ? { path: v, label: v, hint: false } : { path: v.path, label: v.label || v.path, hint: !!v.hint }
  const lookupRedirect = (redirects, term) => {
    if (!redirects || !term) return null
    const tries = [term]
    if (/es$/.test(term)) tries.push(term.slice(0, -2))
    if (/s$/.test(term)) tries.push(term.slice(0, -1))
    for (const t of tries) if (Object.prototype.hasOwnProperty.call(redirects, t)) return entryOf(redirects[t])
    return null
  }
  const singular = (w) =>
    /ies$/.test(w) ? [w, w.slice(0, -3) + 'y', w.slice(0, -1)] : /es$/.test(w) ? [w, w.slice(0, -1), w.slice(0, -2)] : /s$/.test(w) ? [w, w.slice(0, -1)] : [w]
  const lookupWord = (table, w) => {
    if (!table) return null
    for (const t of singular(w)) if (Object.prototype.hasOwnProperty.call(table, t)) return table[t]
    return null
  }
  const pathnameOf = (href) => {
    try {
      return new URL(href, location.href).pathname.replace(/\/$/, '')
    } catch (e) {
      return ''
    }
  }

  /* ---------- the hint row ("Looking for Herb Seeds?") ---------- */
  const renderHint = (entry) => {
    // the server-rendered collection banner may already link the same place
    const banner = document.querySelector('.rs-search-collections')
    if (banner && !/\?/.test(entry.path)) {
      const want = pathnameOf(entry.path)
      for (const a of banner.querySelectorAll('a[href]')) if (pathnameOf(a.getAttribute('href')) === want) return
    }
    const p = el('p', 'rs-search-hint__text')
    p.appendChild(document.createTextNode('Looking for '))
    const a = el('a', 'rs-search-hint__link', entry.label)
    a.href = entry.path
    p.appendChild(a)
    p.appendChild(document.createTextNode('?'))
    // zero page: a SIBLING above the host, never inside it - render() and showEmpty() remove or
    // rewrite the host's children, and the hint is the only reason a hint:true entry exists
    if (isZero) host.before(p)
    else {
      host.appendChild(p)
      host.hidden = false
    }
  }

  /* rareseeds 2026-10-21: the facet hint ("Looking for tomato varieties that are red? See them").
     Same placement rules as renderHint; the clause is built from JSON labels + typed words, textContent. */
  const facetSentence = (dest) =>
    (dest.kind === 'collection' ? dest.crop + ' varieties' : dest.kind === 'search' ? 'varieties matching “' + dest.crop + '”' : 'varieties') +
    ' that ' +
    dest.clause
  const renderFacetHint = (dest) => {
    const p = el('p', 'rs-search-hint__text')
    p.appendChild(document.createTextNode('Looking for ' + facetSentence(dest) + '? '))
    const a = el('a', 'rs-search-hint__link', 'See them →')
    a.href = dest.url
    p.appendChild(a)
    if (isZero) host.before(p)
    else {
      host.appendChild(p)
      host.hidden = false
    }
  }
  const sameUrl = (a, b) => {
    try {
      const x = new URL(a, location.href)
      const y = new URL(b, location.href)
      return x.pathname.replace(/\/$/, '') === y.pathname.replace(/\/$/, '') && x.search === y.search
    } catch (e) {
      return false
    }
  }

  /* ---------- results page: hint only ---------- */
  if (!isZero) {
    fetchJson(host.dataset.intentsUrl).then((intents) => {
      clearTimeout(timer)
      const entry = lookupRedirect(intents && intents.redirects, normalize(terms))
      if (entry) {
        renderHint(entry)
        return
      }
      // a2 as a hint, never a navigation: "red tomato" matches 26 names and the colour facet both
      const collections = collectionsList()
      const dest = facetDestination(parseFacets(terms, intents, collections), collections)
      if (dest && !sameUrl(dest.url, location.href)) renderFacetHint(dest)
    })
    return
  }

  /* ---------- zero-result page ---------- */
  const showEmpty = () => {
    if (rendered) return
    rendered = true
    // keep the live region the reader is tracking and CHANGE it (unhiding the block below is not an
    // announced change); visually hidden so the sentence is not printed twice
    if (status) {
      status.textContent = 'No results for “' + terms + '”.'
      status.classList.add('visually-hidden')
    }
    if (empty) empty.hidden = false
    if (didYouMean) host.insertBefore(didYouMeanNode(), empty)
    retiredNote()
    closestMatches()
  }
  /* h. (2026-10-26) "we have carried this before": the ShopifyQL log for 10/21-24 showed 466 unclicked
     searches in four days for varieties that exist in the admin as ARCHIVED (ginger plants, foxglove,
     shiso, larkspur, San Marzano, Carolina Reaper …) - a bare "No results" told those shoppers nothing.
     assets/rs-search-retired.json (tools/gen-search-retired.mjs) carries every word of a retired title
     that no active title uses, with one example title and the crop collection. Looked up only on the
     empty state, lazily; the note goes above the empty block, textContent only. */
  let retiredAlso = [] // words the pipeline arrived at (spelling / alias) - tumeric -> turmeric
  const retiredNote = () => {
    const url = host.dataset.retiredUrl
    if (!url || !empty) return
    fetchJson(url).then((data) => {
      const table = data && data.w
      const titles = data && data.t
      const cols = (data && data.c) || []
      if (!table || !titles || empty.hidden) return
      const typed = positives(tokenize(terms)).map((t) => t.w)
      for (const w of retiredAlso) if (typed.indexOf(w) === -1) typed.push(w)
      let best = null
      for (const w of typed) {
        for (const cand of singular(w)) {
          if (!Object.prototype.hasOwnProperty.call(table, cand)) continue
          for (const ex of table[cand] || []) {
            const title = titles[ex[0]]
            if (!title) continue
            const folded = fold(title)
            const cover = typed.filter((x) => folded.indexOf(x) !== -1).length
            const col = ex[1] >= 0 ? cols[ex[1]] : null
            // strictly better coverage wins; ties keep the generator's ranking (family position first)
            if (!best || cover > best.cover) best = { title, col, cover }
          }
        }
      }
      if (!best) return
      const p = el('p', 'rs-search-note rs-search-note--retired')
      p.appendChild(document.createTextNode('We have carried “' + best.title + '” before, but it is not in the catalog right now.'))
      if (best.col && /^[a-z0-9][a-z0-9-]*$/.test(best.col[0])) {
        p.appendChild(document.createTextNode(' Browse '))
        const a = el('a', null, best.col[1] || best.col[0])
        a.href = '/collections/' + best.col[0]
        p.appendChild(a)
        p.appendChild(document.createTextNode(' for what is available.'))
      }
      host.insertBefore(p, empty)
    })
  }
  const didYouMeanNode = () => {
    const p = el('p', 'rs-search-note rs-search-note--alt')
    p.appendChild(document.createTextNode('Did you mean '))
    const a = el('a', null, '“' + didYouMean.text + '”')
    a.href = didYouMean.href
    p.appendChild(a)
    p.appendChild(document.createTextNode('?'))
    return p
  }

  /* Insert a section render in place of the host. Markup is inserted as a STRING (like
     util.ajax-renderer) and every module script it carries is run once: the zero-result page never
     loaded list.filter-grid, the view buttons, quick shop or the card swatches, and module imports are
     idempotent so running them is always safe. */
  const render = (res, noteText, extras) => {
    rendered = true
    // the server-rendered status element becomes the note: one live region from first paint to the
    // final sentence, so the outcome is announced as a change (a freshly inserted, pre-filled
    // role=status is not reliably read by NVDA / JAWS / VoiceOver)
    const note = status || el('p')
    note.className = 'rs-search-note'
    note.textContent = noteText
    note.setAttribute('role', 'status')
    const newCount = res.doc.querySelector('[data-collection-count]')
    if (newCount) {
      const countText = newCount.textContent.trim()
      document.querySelectorAll('[data-collection-count]').forEach((n) => {
        n.textContent = countText
      })
      // the tab title still says "0 results found" from the title-scoped render
      document.title = document.title.replace(/\b0 results?\b/, countText)
    }
    host.insertAdjacentHTML('beforebegin', res.content.outerHTML)
    const inserted = host.previousElementSibling
    inserted.before(note)
    top = note
    if (extras && extras.alt) {
      const p = el('p', 'rs-search-note rs-search-note--alt')
      p.appendChild(document.createTextNode(extras.alt.prefix))
      const a = el('a', null, '“' + extras.alt.text + '”')
      a.href = extras.alt.href
      p.appendChild(a)
      inserted.before(p)
    }
    if (didYouMean) inserted.before(didYouMeanNode())
    host.remove()
    const seen = new Set()
    const run = (script) => {
      const text = script.textContent.trim()
      if (!text || seen.has(text)) return
      seen.add(text)
      const s = document.createElement('script')
      s.type = 'module'
      s.textContent = text
      document.head.appendChild(s)
    }
    inserted.querySelectorAll('template[data-island]').forEach((t) => {
      t.content.querySelectorAll('script[type="module"]').forEach(run)
    })
    inserted.querySelectorAll('script[type="module"]').forEach(run)
    history.replaceState(history.state, '', res.href)
    if (extras && extras.h1) {
      const h1 = document.querySelector('.section-header__title')
      if (h1) h1.textContent = 'Search results for “' + extras.h1 + '”'
    }
  }

  // one Section Rendering fetch of the search section at `u` (a URL object); `href` is what the
  // address bar becomes when its render is shown
  const renderFetchUrl = (u, href, noStore) => {
    u.searchParams.set('section_id', sectionId)
    return fetch(u.toString(), { headers: { Accept: 'text/html' }, signal, cache: noStore ? 'no-store' : 'default' })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status))
        return r.text()
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html')
        const content = doc.querySelector('.main-search__content')
        const cards = content ? content.querySelectorAll('.product-grid .grid-item').length : 0
        const countEl = doc.querySelector('[data-collection-count]')
        const count = countEl
          ? parseInt((countEl.textContent.match(/\d[\d,]*/) || ['0'])[0].replace(/,/g, ''), 10)
          : cards
        return { doc, content, cards, count, href }
      })
  }
  // SF-002: an unscoped q is the broad pass - its address-bar href carries the rs=broad marker
  const renderFetch = (q, noStore) => renderFetchUrl(pageUrl(q), pageHref(q, !SCOPED_Q.test(q)), noStore)
  // rareseeds 2026-10-21: the search section with a title query AND facet filter params
  const renderFetchFacets = (q, filters) => {
    const u = pageUrl(q)
    for (const f of filters) for (const v of f.values) u.searchParams.append(FACET_PARAM + f.key, v)
    return renderFetchUrl(u, u.pathname + u.search, false)
  }
  const usable = (res) => res && res.content && res.cards > 0 && res.count < NOISE_CAP

  /* ---------- f: "Looking for…" (collections / pages / articles) ---------- */
  const suggestDoc = (q, types, limit) => {
    const u = new URL(searchPath.replace(/\/$/, '') + '/suggest', location.origin)
    u.searchParams.set('q', q)
    u.searchParams.set('resources[type]', types)
    u.searchParams.set('resources[limit]', String(limit))
    u.searchParams.set('resources[limit_scope]', 'each')
    u.searchParams.set('section_id', 'search-results')
    return fetch(u.toString(), { headers: { Accept: 'text/html' }, signal })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch(() => null)
  }
  const copyList = (doc, selector, heading) => {
    const links = doc.querySelectorAll('.results__group-1 ' + selector + ' ul li a[href]')
    if (!links.length) return null
    const group = el('div', 'rs-search-related__group')
    group.appendChild(el('h2', 'rs-search-related__heading', heading))
    const ul = el('ul', 'rs-search-related__list')
    links.forEach((src) => {
      const li = el('li')
      const a = el('a', null, src.textContent.trim())
      a.href = src.getAttribute('href')
      li.appendChild(a)
      ul.appendChild(li)
    })
    group.appendChild(ul)
    return group
  }
  const lookingFor = (q) =>
    suggestDoc(q, 'collection,page,article', 3).then((doc) => {
      if (!doc) return
      const groups = [
        copyList(doc, '.results--collections', 'Collections'),
        copyList(doc, '.results--pages', 'Pages'),
        copyList(doc, '.results--articles', 'Guides & articles'),
      ].filter(Boolean)
      if (!groups.length) return
      const nav = el('nav', 'rs-search-related')
      nav.setAttribute('aria-label', 'Related collections and pages')
      nav.appendChild(el('p', 'rs-search-related__kicker', 'Looking for…'))
      groups.forEach((g) => nav.appendChild(g))
      // above whatever the product pipeline rendered (or renders later): a grid inserted later lands
      // between this block and the host, which keeps the block on top either way.
      if (top.parentNode) top.parentNode.insertBefore(nav, top)
    })

  /* ---------- g: "Closest matches" under the empty state, gated on a title-word match ---------- */
  let gateWords = []
  const closestMatches = () => {
    if (!gateWords.length) return
    const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const matchers = gateWords.map((w) => new RegExp('(^|[^a-z0-9])' + escapeRe(w), 'i'))
    suggestDoc(gateWords.join(' '), 'product', 4).then((doc) => {
      if (!doc) return
      const items = []
      doc.querySelectorAll('.results--products ul li a[href]').forEach((a) => {
        const titleEl = a.querySelector('.results-products__info > span:not(.grid-product__vendor)')
        const title = (titleEl || a).textContent.trim()
        if (!title || !matchers.some((re) => re.test(fold(title)))) return
        items.push({ href: a.getAttribute('href'), title })
      })
      if (!items.length) return
      const section = el('section', 'rs-search-closest')
      section.appendChild(el('h2', 'rs-search-closest__heading', 'Closest matches'))
      const ul = el('ul', 'rs-search-closest__list')
      items.forEach((it) => {
        const li = el('li')
        const a = el('a', null, it.title)
        a.href = it.href
        li.appendChild(a)
        ul.appendChild(li)
      })
      section.appendChild(ul)
      host.appendChild(section)
    })
  }

  /* ---------- c: vocab correction — see correctWord() at module scope (exported for tests) ---------- */
  const known = (vocab, w) => singular(w).some((t) => Object.prototype.hasOwnProperty.call(vocab, t))
  const correct = (vocab, ignore, w) => correctWord(vocab, ignore, w)

  /* ---------- the pipeline ---------- */
  const broadHref = pageHref(terms, true) // SF-002: broad on purpose, see pageUrl
  const original = tokenize(terms)
  const originalScoped = scoped(original)
  gateWords = positives(original).map((t) => t.w)

  const vocabPromise = { p: null }
  const loadVocab = () => {
    if (!vocabPromise.p) vocabPromise.p = fetchJson(host.dataset.vocabUrl)
    return vocabPromise.p
  }
  // the retired-variety word table (assets/rs-search-retired.json, ~135 KB): fetched once, only when a
  // typed word is unknown to the active vocabulary or the empty state renders
  const retiredPromise = { p: null }
  const loadRetired = () => {
    if (!host.dataset.retiredUrl) return Promise.resolve(null)
    if (!retiredPromise.p) retiredPromise.p = fetchJson(host.dataset.retiredUrl).then((d) => (d && d.w ? d.w : null))
    return retiredPromise.p
  }

  const pipeline = async () => {
    const intents = (await fetchJson(host.dataset.intentsUrl)) || {}
    const redirects = intents.redirects || {}
    const spelling = intents.spelling || {}
    const aliases = intents.aliases || {}
    const ignore = new Set(intents.ignore || [])

    // location.replace after window load + grace, capped at REDIRECT_CAP_MS (PGL-055, see scheduleRedirect)
    const leaveFor = (path) =>
      scheduleRedirect(path, {
        readyState: document.readyState,
        onLoad: (fn) => window.addEventListener('load', fn, { once: true }),
      })

    // a. curated destination
    const term = normalize(terms)
    const entry = lookupRedirect(redirects, term)
    if (entry && !entry.hint) {
      setStatus('Taking you to ' + entry.label + '…')
      leaveFor(entry.path)
      return
    }
    if (entry) renderHint(entry)

    // a2. facet phrases (rareseeds 2026-10-21) - "tomatoes that mature in 60 days", "beans that climb"
    const collections = collectionsList()
    const dest = facetDestination(parseFacets(terms, intents, collections), collections)
    if (dest) {
      const sentence = facetSentence(dest)
      if (dest.kind === 'all') {
        setStatus('Taking you to every variety that ' + dest.clause + '…')
        leaveFor(dest.url)
        return
      }
      setStatus('Looking for ' + sentence + '…')
      // review #1: only a probe that ANSWERED with zero cards may relax to the whole catalog; a
      // thrown / aborted fetch says nothing about the names and falls through to step b
      const probe = { ok: false, res: null }
      try {
        probe.res = await renderFetchFacets(dest.probeQ, dest.filters)
        probe.ok = true
      } catch (e) {
        probe.ok = false
      }
      const decision = facetProbeDecision(dest, probe)
      if (decision === 'navigate') {
        setStatus('Taking you to ' + sentence + '…')
        leaveFor(dest.url)
        return
      }
      if (decision === 'render') {
        gateWords = positives(tokenize(dest.crop)).map((t) => t.w)
        probe.res.href = dest.url
        render(probe.res, 'Showing ' + sentence + '.')
        lookingFor(dest.crop)
        return
      }
      if (decision === 'relax') {
        setStatus('No “' + dest.crop + '” names matched; showing every variety that ' + dest.clause + '…')
        leaveFor(dest.relaxUrl)
        return
      }
      // 'fallthrough': the probe failed, or only a colour / department matched and nothing carries
      // it with these names - the title pipeline below is the better answer (b-e)
    }

    // b. spelling + aliases. Phrases first (2026-10-27, PGL-052): "egg plant" is one crop, not the
    // word "egg" with plant(s) dropped - see applyPhraseSpelling
    const phrased = applyPhraseSpelling(spelling, terms)
    let tokens = (phrased === terms ? original : tokenize(phrased)).map((t) => {
      if (t.neg) return t
      const fixed = lookupWord(spelling, t.w)
      const w = typeof fixed === 'string' ? fixed : t.w
      const alt = lookupWord(aliases, w)
      return { w, neg: false, alt: Array.isArray(alt) && alt.length ? alt.slice() : null }
    })
    const spelt = tokens.map((t) => (t.neg ? '-' + t.w : t.w)).join(' ')
    retiredAlso = positives(tokens).map((t) => t.w)
    const spellingChanged = tokens.length !== original.length || tokens.some((t, i) => t.w !== original[i].w)
    gateWords = positives(tokens).map((t) => t.w)
    loadVocab() // prefetch, it is a static CDN asset
    lookingFor(positives(tokens).map((t) => t.w).join(' ') || terms) // f, in parallel

    if (spellingChanged) {
      // the corrected term may itself be a curated destination (perinnial -> perennial)
      const again = lookupRedirect(redirects, normalize(spelt))
      if (again && !again.hint) {
        setStatus('Taking you to ' + again.label + '…')
        leaveFor(again.path)
        return
      }
    }
    setStatus('No product names matched “' + terms + '”. Looking for similar names…')
    const q = scoped(tokens)
    if (q !== originalScoped && q !== searchedQ) {
      const res = await renderFetch(q, false)
      if (usable(res)) {
        // an alias keeps the typed word in the h1 ("brassica"); a spelling fix shows the fixed word
        render(res, 'Showing results for “' + shown(tokens) + '”.', { h1: hasAlias(tokens) ? null : shown(tokens) })
        return
      }
    }

    // c. nearest title word (2026-10-26: prefix completion, partial fixes, every fix that finds
    //    something is rendered; a word nothing is named is dropped and said so, not OR-ed in)
    const vocabJson = await loadVocab()
    const vocab = vocabJson && vocabJson.words ? vocabJson.words : null
    let corrected = null
    if (vocab) {
      const unknown = positives(tokens).filter((t) => !t.alt && !known(vocab, t.w))
      if (unknown.length) {
        // a word of a RETIRED variety is a real word, not a typo: "deer resistant" must not become
        // "deep" - it stays, and when nothing is named that way the empty state says we carried it
        const retiredWords = (await loadRetired()) || {}
        const isRetired = (w) => singular(w).some((v) => Object.prototype.hasOwnProperty.call(retiredWords, v))
        const fixes = new Map()
        const keptUnfixed = new Set() // curated, retired or rare words: real words, kept as typed
        for (const t of unknown) {
          // a word with a curated entry is never a typo of a title word (bulk is not bull), and a
          // retired variety's word is a real word the empty state can name
          if (lookupRedirect(redirects, t.w) || isRetired(t.w)) { keptUnfixed.add(t); continue }
          const f = correct(vocab, ignore, t.w)
          if (f && !f.weak) fixes.set(t, f)
          else if (f && f.weak) keptUnfixed.add(t) // "krimm" is one letter off a rare real "krim": not a typo
        }
        // only a word with NO candidate at all is dropped ("Nothing is named ...")
        const unfixable = unknown.filter((t) => !fixes.has(t) && !keptUnfixed.has(t))
        const kept = tokens.filter((t) => t.neg || unfixable.indexOf(t) === -1)
        if (positives(kept).length && (fixes.size || unfixable.length)) {
          const rewritten = kept.map((t) => (fixes.has(t) ? { w: fixes.get(t).w, neg: false, alt: null } : t))
          const changed = scoped(rewritten) !== originalScoped && scoped(rewritten) !== searchedQ
          if (changed) {
            const res = await renderFetch(scoped(rewritten), false)
            if (usable(res)) {
              gateWords = positives(rewritten).map((t) => t.w)
              const droppedNote = unfixable.length ? ' Nothing is named \u201c' + unfixable.map((t) => t.w).join(' ') + '\u201d.' : ''
              render(res, 'Showing results for \u201c' + shown(rewritten) + '\u201d.' + droppedNote, {
                h1: shown(rewritten),
                alt: { prefix: 'Search instead for ', text: terms, href: broadHref },
              })
              return
            }
            // a "Did you mean" link only to a page that HAS results (noisy, over the cap) - never to an
            // empty one (review 2026-10-26 #3: "stupice tomato" linked to a dead "spice tomato")
            if (fixes.size && res && res.cards > 0) didYouMean = { text: shown(rewritten), href: pageHref(scoped(rewritten)) }
          }
          if (fixes.size) corrected = rewritten
        }
        // a rare real word one letter off ("krimm" / Black Krim) is tried as a fix before anything
        // else gives up on it - with the "Search instead for" way out, since it IS a guess
        const weakFixes = new Map()
        for (const t of unknown) {
          if (!keptUnfixed.has(t) || lookupRedirect(redirects, t.w) || isRetired(t.w)) continue
          const f = correct(vocab, ignore, t.w)
          if (f && f.weak) weakFixes.set(t, f)
        }
        if (weakFixes.size) {
          const guessed = (corrected || tokens).map((t) => (weakFixes.has(t) ? { w: weakFixes.get(t).w, neg: false, alt: null } : t))
          const gq = scoped(guessed)
          if (gq !== originalScoped && gq !== searchedQ) {
            const res = await renderFetch(gq, false)
            if (usable(res)) {
              gateWords = positives(guessed).map((t) => t.w)
              render(res, 'Showing results for \u201c' + shown(guessed) + '\u201d.', {
                h1: shown(guessed),
                alt: { prefix: 'Search instead for ', text: terms, href: broadHref },
              })
              return
            }
          }
        }
      }
    }
    const working = corrected || tokens

    // d. the head noun, then any of the words. English puts the crop LAST ("acorn squash", "giant
    //    pumpkin", "hot sauce"): when every word is a real title word but no name carries them all,
    //    the last word alone is the honest answer (2026-10-26); the OR of every word only after that,
    //    and only when every word is a real title word (else the OR is junk: "super hot" used to show
    //    72 names with "super" or "hot" in them)
    const pos = positives(working)
    if (pos.length >= 2 && vocab) {
      // the crop word is the one naming an A-Z collection ("collard greens" -> Collard Seeds, not
      // greens; "california poppies" -> Poppy Seeds); failing that the last word the titles know
      const cols = collectionsList()
      const cropWordsTyped = pos.filter((t) => !t.alt && matchCollections([singularCrop(t.w)], cols).length > 0)
      const knownWords = pos.filter((t) => t.alt || known(vocab, t.w))
      const head = cropWordsTyped.length === 1 ? cropWordsTyped[0] : knownWords.length ? knownWords[knownWords.length - 1] : null
      const headQ = head ? scoped(working.filter((t) => t.neg || t === head)) : originalScoped
      if (headQ !== originalScoped && headQ !== searchedQ) {
        const res = await renderFetch(headQ, false)
        if (usable(res)) {
          gateWords = [head.w]
          render(res, 'No product names matched all of \u201c' + terms + '\u201d. Showing \u201c' + shown([head]) + '\u201d.')
          return
        }
      }
    }
    if (pos.length >= 2 && (!vocab || pos.every((t) => t.alt || known(vocab, t.w)))) {
      const orQ =
        pos.map((t) => (t.alt ? t.alt.map(field).join(' OR ') : field(t.w))).join(' OR ') +
        working
          .filter((t) => t.neg)
          .map((t) => ' -' + field(t.w))
          .join('')
      const res = await renderFetch(orQ, false)
      if (usable(res)) {
        // the typed words first; name the rewrite when a step changed one, or the sentence reads as
        // if the site misquoted the shopper
        render(
          res,
          corrected || spellingChanged
            ? 'No product names matched all of \u201c' + terms + '\u201d. Showing names that match any of \u201c' + shown(working) + '\u201d.'
            : 'No product names matched all of \u201c' + shown(working) + '\u201d. Showing names that match any of them.'
        )
        return
      }
    }

    // e. descriptions at last resort
    setStatus('No product names matched “' + terms + '”. Looking in descriptions…')
    // the words as typed, unless spelling / vocab fixed one (tumeric -> turmeric finds the turmeric
    // recipes and growing copy; the typed misspelling found 82 cucumbers)
    const broadTokens = corrected || (spellingChanged ? tokens : null)
    const broadQ = broadTokens ? words(broadTokens) : terms
    const single = pos.length === 1 && !working.some((t) => t.neg)
    const cap = single ? NOISE_CAP_SINGLE : NOISE_CAP
    let res = await renderFetch(broadQ, false)
    if (res.count >= cap) {
      let ok = false
      for (let attempt = 1; attempt <= 2 && !ok; attempt++) {
        res = await renderFetch(broadQ, true)
        ok = res.count > 0 && res.count < cap
      }
      if (!ok) {
        showEmpty()
        return
      }
    }
    if (!res.content || !res.cards) {
      showEmpty()
      history.replaceState(history.state, '', pageHref(broadQ, true)) // SF-002: rs=broad, see pageUrl
      return
    }
    // the typed term stays in the first clause (it is what the h1 and the field say); the second
    // names the word actually searched when a step changed it
    render(
      res,
      broadTokens
        ? 'No product names matched “' + terms + '”. Showing descriptions that mention “' + broadQ + '”.'
        : 'No product names matched “' + terms + '”. Showing matches in descriptions.'
    )
  }

  pipeline()
    .catch(() => showEmpty())
    .then(() => clearTimeout(timer))
}
